import { effect, ElementRef, inject, Injectable, Renderer2, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import * as joint from '@clientio/rappid';
import { ProposedComponentChooserDialogComponent } from 'guideline/feature-component-search';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { FlowchartSidebarStoreService } from 'libs/guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { BehaviorSubject, filter, skip, Subscription } from 'rxjs';
import { LinksService, ScrollService, SelectionService } from 'shared/domain';
import { DialogService } from 'shared/ui-common';
import { deepEqual, generateMD5 } from 'shared/util-common';

import {
  ComponentDto,
  ComponentType,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  GuidelineChapterType,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { renderCommentBadges } from './comment-badge.util';
import { FlowchartEventsService } from './flowchart-events.service';
import { FlowchartSearchService } from './flowchart-search.service';
import { FlowchartConfig } from './flowchart.config';
import { AnchoredFlowchartComment, CommentType } from '../../../../../tt-comments/src/lib/comment.model';
import { FlowchartElementsSubjectStore } from '../data/flowchart-elements-subject-store.service';
import { FlowchartManagerService } from '../data/flowchart-manager.service';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartScrollerService } from '../flowchart/scroller.service';
import { FlowchartViewService } from '../flowchart/view.service';
import { ContainerNodeService } from '../operations/container-node.service';
import ElementActivitiesTransactionService from '../operations/element-activities-toolbar/element-activities-transaction.service';
import { FlowchartLoaderService } from '../operations/flowchart-loader.service';
import { connector } from '../operations/link/custom-connector/CustomConnector';
import { CustomRouter } from '../operations/link/custom-router/CustomRouter';
import { LinkValidator } from '../operations/link/validator/LinkValidator';
import NodePickerTransactionService from '../operations/node-picker-toolbar/node-picker-transaction.service';
import OperationsService from '../operations/operations.service';
import { SizeService } from '../operations/size.service';
import SubflowService from '../operations/subflow.service';
import { SelectionBehaviorElement, SelectionBehaviorElementView } from '../shapes/selection-behavior';
import { HeaderElement } from '../shapes/shared-header';

/**
 * Options for initializing the flowchart service.
 * @property allowEvents - Whether to allow user interactions with the flowchart.
 * @property allowInlineComments - Whether to show "Add Comment" popup when an element is clicked.
 * @property mobileView - Whether the flowchart is being viewed on a mobile device
 */
export type FlowchartInitOptions = {
  allowEvents: boolean;
  allowInlineComments?: boolean;
  mobileView?: boolean;
};

@Injectable()
export class FlowchartService {
  private flowchartLoaderService = inject(FlowchartLoaderService);
  private sidebarService = inject(LegacySidebarService);
  private flowchartSidebarStoreService = inject(FlowchartSidebarStoreService);
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private flowchartScrollerService = inject(FlowchartScrollerService);
  private operationsService = inject(OperationsService);
  private subflowService = inject(SubflowService);
  private flowchartEventsService = inject(FlowchartEventsService);
  private linksService = inject(LinksService);
  private selectionService = inject(SelectionService);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private sizeService = inject(SizeService);
  private flowchartManagerService = inject(FlowchartManagerService);
  private flowchartSearchService = inject(FlowchartSearchService);
  private nodePickerTransactionService = inject(NodePickerTransactionService);
  private elementActivitiesTransactionService = inject(ElementActivitiesTransactionService);
  private scrollService = inject(ScrollService);

  private flowchartElementsSubjectStore = inject(FlowchartElementsSubjectStore);

  public graph: joint.dia.Graph;
  public paper: joint.dia.Paper;
  public scroller: joint.ui.PaperScroller;
  private dtoSubscriber: Subscription;
  private storeSubscriber: Subscription;
  private viewportAnchor: joint.g.PlainPoint | null = null;
  private paperSource: BasePaper;
  private graphSource: BaseGraph;

  protected selectedFlowchartElement = this.selectionService.selection;

  public flowchartElements = toSignal(this.flowchartElementsSubjectStore.flowchartElementsSubject);

  public comments = signal<AnchoredFlowchartComment[]>([]);
  public selectedCommentId = signal<string>(undefined);

  public freezeFlowchartSubject = new BehaviorSubject<boolean>(false);

  private flowchartId: string;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();

    effect(() => {
      const comments = this.comments();
      const selectedCommentId = this.selectedCommentId();

      if (!this.graph) {
        return;
      }

      this.handleComments(comments, selectedCommentId);
    });
  }

  public init(flowchartId: string, el: HTMLElement, options?: FlowchartInitOptions) {
    const namespace = {
      ...joint.shapes,
      treetop: { SelectionBehaviorElement, SelectionBehaviorElementView, HeaderElement },
    };

    const graph = (this.graph = new joint.dia.Graph({}, { cellNamespace: namespace }));

    const paper = (this.paper = new joint.dia.Paper({
      model: graph,
      width: FlowchartConfig.paper.maxWidth,
      height: FlowchartConfig.paper.baseHeight,
      cellViewNamespace: namespace,
      drawGrid: options?.allowEvents,
      async: true,
      sorting: joint.dia.Paper.sorting.APPROX,
      background: { color: FlowchartConfig.paper.background },
      preventDefaultBlankAction: !options?.mobileView,
      snapLabels: true,
      clickThreshold: 4,
      snapLinks: true,
      interactive: options?.allowEvents
        ? {
            labelMove: true,
            linkMove: false,
            elementMove: true,
          }
        : false,
      gridSize: FlowchartConfig.paper.gridSize,
      defaultConnectionPoint: {
        name: 'anchor',
      },
      defaultRouter: function (vertices, args, linkView) {
        return CustomRouter.router(vertices, linkView);
      },
      defaultConnector: function (sourcePoint: joint.g.PlainPoint, targetPoint, vertices) {
        return connector(sourcePoint, targetPoint, vertices);
      },
      validateConnection: (cellViewS, magnetS, cellViewT, magnetT, end, linkView) =>
        this.validateConnection(cellViewS, magnetS, cellViewT, magnetT, end, linkView, graph),
    }));

    paper.on('render:done', () => this.refreshCommentBadges());

    this.scroller = new joint.ui.PaperScroller({
      paper,
      autoResizePaper: true,
      cursor: 'grab',
      baseWidth: FlowchartConfig.paper.maxWidth,
      baseHeight: FlowchartConfig.paper.baseHeight,
      scrollWhileDragging: true,
      padding: 40,
      contentOptions: {
        padding: 40,
        allowNewOrigin: 'any',
        allowNegativeBottomRight: true,
        useModelGeometry: true,
      },
    });

    el.appendChild(this.scroller.el);

    this.scroller.adjustPaper();

    this.flowchartViewService.setJointJsPaper(paper);

    this.flowchartModelService.setJointJsGraph(graph);

    this.flowchartId = flowchartId;
    this.flowchartScrollerService.init(flowchartId, this.scroller);
    const scrollRegisterFlowchartId = flowchartId.endsWith('-flowchart') ? flowchartId.slice(0, -'--flowchart'.length) : flowchartId;
    this.scrollService.registerFlowchart(scrollRegisterFlowchartId);
    this.flowchartEventsService.init(flowchartId);
    this.nodePickerTransactionService.init(flowchartId);
    this.elementActivitiesTransactionService.init(flowchartId);
    this.flowchartSearchService.init(flowchartId);

    this.operationsService.setDOMReferences(this.renderer, this.el);

    this.loadFlowchart(true, options?.allowEvents);

    this.setFlowchartEvents(options?.allowEvents, options?.mobileView, options?.allowInlineComments);

    this.linksService.links.state$.subscribe(() => {
      if (this.selectedFlowchartElement()) {
        this.operationsService.addElementTags(this.selectedFlowchartElement().element);
      }
    });

    this.dtoSubscriber = this.flowchartSidebarStoreService
      .getCurrentDto()
      .pipe(
        skip(1),
        filter((dto) => {
          if (dto?.elementType === FlowchartElementType.FlowchartLink) {
            const result = this.sidebarService.deepSearchElements(this.flowchartElementsSubjectStore.getValue(), dto.fromFlowchartNodeId);
            return !!result;
          }
          return true;
        }),
      )
      .subscribe((dto) => {
        this.updateElement(dto);
      });

    this.storeSubscriber = this.flowchartManagerService.currentSearchResult.pipe(skip(1)).subscribe((component) => {
      this.handleSearchResult(component);
    });

    this.handleComments(this.comments(), this.selectedCommentId());
  }

  public updateElements(elements: FlowchartNodeDto[]) {
    const currentElements = this.flowchartElementsSubjectStore.getValue();

    const processElements = (el: FlowchartNodeDto) => {
      this.updateElement(el);

      if (el.children && el.children.length > 0) {
        el.children.forEach((child) => {
          processElements(child);
        });
      }
    };

    currentElements?.forEach((element) => {
      const updatedElement = elements?.find((el) => el.id === element.id);
      if (updatedElement && !deepEqual(element, updatedElement)) {
        processElements(updatedElement);
      } else if (!updatedElement) {
        this.graph?.getCell(element.id)?.remove();
      }
    });
  }

  public setElements(elements: FlowchartNodeDto[]) {
    this.flowchartElementsSubjectStore.next(elements);
  }

  public updateElement(dto: Partial<FlowchartNodeDto & FlowchartLinkDto>) {
    if (!dto) {
      return;
    }

    if (dto.elementType === FlowchartElementType.FlowchartContainer) {
      this.sidebarService.updateFlowchart(
        this.flowchartElementsSubjectStore,
        dto,
        this.operationsService.editHeaderTitle.bind(this.operationsService),
        this.operationsService.setLinkAttribute.bind(this.operationsService),
        null,
        null,
        null,
        this.operationsService.editSelectionBehavior.bind(this.operationsService),
        this.operationsService.editColorPreset.bind(this.operationsService),
      );

      this.setSelectedStyles(dto);
      this.operationsService.addElementTags(dto);

      dto.outgoingLinks?.forEach((flowchartLink) => {
        this.operationsService.setLinkAttribute(flowchartLink.attribute, flowchartLink.id);
        this.operationsService.setLinkOffset(flowchartLink.linkOffset, flowchartLink.id);
      });
    } else if (dto) {
      if (
        dto.elementType === FlowchartElementType.FlowchartAction ||
        dto.elementType === FlowchartElementType.FlowchartDecisionNode ||
        dto.elementType === FlowchartElementType.FlowchartStartNode ||
        dto.elementType === FlowchartElementType.FlowchartTextBlock ||
        dto.elementType === FlowchartElementType.FlowchartSubflow
      ) {
        this.operationsService.addElementTags(dto);
      }

      this.sidebarService.updateFlowchart(
        this.flowchartElementsSubjectStore,
        dto,
        this.operationsService.editTitle.bind(this.operationsService),
        this.operationsService.setLinkAttribute.bind(this.operationsService),
        this.operationsService.editTextBox.bind(this.operationsService),
        this.operationsService.initElementsDescription.bind(this.operationsService),
        this.operationsService.removeElementsDescription.bind(this.operationsService),
        null,
        this.operationsService.editColorPreset.bind(this.operationsService),
      );

      dto.outgoingLinks?.forEach((flowchartLink) => {
        const flowchartElements = this.flowchartElementsSubjectStore.getValue();

        if (dto.elementType === FlowchartElementType.FlowchartDecisionNode) {
          const unit = this.sidebarService.getUnitFromSourceLinkDecision(flowchartLink.fromFlowchartNodeId, flowchartElements);
          const node = this.sidebarService.deepSearchElements(flowchartElements, dto.fromFlowchartNodeId);
          const onlyTruePaths = this.sidebarService.decisionNodeHasOnlyTruePaths(node?.outgoingLinks);

          node?.outgoingLinks.forEach((link) => {
            if (onlyTruePaths) {
              this.operationsService.showLabel(false, link.id, 0);
            } else {
              this.operationsService.setLinkAttribute(link.attribute, link.id, unit);
            }
          });
        } else {
          this.operationsService.setLinkAttribute(flowchartLink.attribute, flowchartLink.id);
        }

        this.operationsService.setLinkOffset(flowchartLink.linkOffset, flowchartLink.id);
      });

      this.setSelectedStyles(dto);
    }
  }

  setFlowchartEvents(allowEvents: boolean, mobileView = false, allowInlineComments = false) {
    if (allowEvents) {
      this.freezeFlowchartSubject.subscribe((value) => {
        if (!value) {
          this.paperSource.paper.freeze();
          this.paperSource.paper.updateViews();
          this.flowchartEventsService.removeEventHandlers();
        } else {
          this.paperSource.paper.unfreeze();
          this.paperSource.paper.updateViews();
          this.flowchartEventsService.initializeEventHandlers({
            allowEvents,
            allowInlineComments,
          });
        }
      });
    } else {
      this.flowchartEventsService.preventInteractiveNodes(mobileView);
      this.flowchartEventsService.initializeEventHandlers({
        allowEvents,
        allowInlineComments,
      });
    }
  }

  loadFlowchart(zoomToFit = true, allowEvents = false) {
    if (this.flowchartElementsSubjectStore.getValue()?.length > 0) {
      this.flowchartLoaderService.loadFlowchartFromDto(
        this.flowchartElementsSubjectStore.getValue(),
        this.operationsService,
        this.graph,
        this.paper,
        this.scroller,
        this.subflowService,
        zoomToFit,
        allowEvents,
      );
      if (allowEvents) {
        const firstElementView = this.paper.findViewByModel(this.graph.getElements()[0]) as joint.dia.ElementView;
        this.flowchartManagerService.setSelectedElementView(firstElementView);
      }
    }
  }

  protected handleSearchResult(componentControl: FormControl | string) {
    const el = this.flowchartManagerService.getSelectedElementView();

    if (!componentControl) {
      //remove node if search dialog is closed without selecting a component
      if (!el.model.attributes.isContainer) {
        this.flowchartLoaderService.removeElementAndAdjustLinksInSubject(
          el.model,
          this.flowchartElementsSubjectStore,
          this.operationsService,
        );
      }
      return;
    }

    //if proposed component is selected
    if (typeof componentControl === 'string') {
      // Check if the element is a container subcomponent
      if (el.model.attributes.isContainerSubComponent) {
        // Let user choose between subdecision and subaction
        this.dialogService
          .open(ProposedComponentChooserDialogComponent, {
            title: 'ChooseTypeForProposedTitle',
            data: { message: 'ChooseTypeForProposedMessage' },
            disableClose: true,
          })
          .afterClosed()
          .subscribe((elementType: FlowchartElementType) => {
            if (elementType === null) {
              this.flowchartLoaderService.removeElementAndAdjustLinksInSubject(
                el.model,
                this.flowchartElementsSubjectStore,
                this.operationsService,
              );
            }
            if (elementType === FlowchartElementType.FlowchartDecisionNode) {
              this.createContainerSubDecision(el);
            }
            const proposed = this.createProposedComponent(componentControl);
            this.handleComponentSelection(proposed);
          });
        return;
      }

      const proposed = this.createProposedComponent(componentControl);
      this.handleComponentSelection(proposed);
      return;
    }

    //if FD/OD is selected remove subaction and create new subdecision
    if (
      el.model.attributes.isContainerSubComponent &&
      componentControl.value.type !== ComponentType.ActivityDefinition &&
      (!componentControl.value.proposedInFlowchartElementType ||
        componentControl.value.proposedInFlowchartElementType !== FlowchartElementType.FlowchartAction)
    ) {
      this.createContainerSubDecision(el);
      this.handleComponentSelection(componentControl);
      return;
    }

    this.handleComponentSelection(componentControl);
  }

  private handleComponentSelection(componentControl: FormControl) {
    const el = this.flowchartManagerService.getSelectedElementView();
    this.flowchartSearchService.handleSearchSelection(componentControl, el);
    this.flowchartLoaderService.setElementSizeInSubject(this.flowchartElementsSubjectStore, el);

    const component = componentControl.value.code?.startsWith('PC')
      ? (componentControl.value as ProposedComponentDto)
      : (componentControl.value as ComponentDto);

    this.flowchartSearchService.assignConvertedMedicalComponentsToSubject(component, el, this.flowchartEventsService);
  }

  createContainerSubDecision(el: joint.dia.ElementView) {
    //if selected element is decision or proposed decision, remove subaction and create new subdecision
    this.flowchartLoaderService.removeElementAndAdjustLinksInSubject(el.model, this.flowchartElementsSubjectStore, this.operationsService);
    const newSubComponent = ContainerNodeService.createContainerSubComponent(this.paperSource.paper, this.graphSource.graph, {
      text: '',
      size: el.model.attributes.size.width,
      type: FlowchartElementType.FlowchartDecisionNode,
      allowEvents: true,
    });

    newSubComponent.set('parentContainerId', el.model.attributes.parentContainerId);

    this.operationsService.addSubComponentToContainer(el.model.attributes.parentContainerId, newSubComponent);
    const newSubComponentElementView = this.paperSource.paper.findViewByModel(newSubComponent) as joint.dia.ElementView;
    this.flowchartManagerService.setSelectedElementView(newSubComponentElementView);
    const container = this.paperSource.paper.findViewByModel(el.model.attributes.parentContainerId);
    this.flowchartLoaderService.addSubComponentToContainerInSubject(this.flowchartElementsSubjectStore, newSubComponent, container);
    this.sidebarService.updateCurrentElement(this.flowchartId, newSubComponent.id as string, this.flowchartElementsSubjectStore.getValue());
    if (el.model.attributes?.subflowParentId) {
      this.sizeService.resizeParentToFit(
        this.graphSource.graph.getCell(el.model.attributes.subflowParentId) as joint.dia.Element,
        this.graphSource.graph,
      );
    }
  }

  createProposedComponent(title: string): FormControl<ProposedComponentDto> {
    const el = this.getFlowchartElement(
      this.flowchartElementsSubjectStore.getValue(),
      this.flowchartManagerService.getSelectedElementView().model.id as string,
    );
    const proposedComponent = new ProposedComponentDto();
    const componentFormControl = new FormControl<ProposedComponentDto>(proposedComponent);
    proposedComponent.title = title;
    proposedComponent.code = 'PC-' + generateMD5(title);
    proposedComponent.proposedInFlowchartElementType = el.elementType;
    proposedComponent.proposedInGuidelineChapterType = GuidelineChapterType.Flowchart;
    componentFormControl.setValue(proposedComponent);

    return componentFormControl;
  }

  setSelectedStyles(dto: Partial<FlowchartNodeDto>) {
    const viewModel = this.graph.getCell(dto.id);

    const commentsOnElement = this.comments().filter((comment) => comment.anchor.elementId === dto.id);
    if (commentsOnElement.length) {
      this.addHighlightsToElement(commentsOnElement, viewModel, this.selectedCommentId());
    }
  }

  getFlowchartElement(elements: FlowchartNodeDto[], id: string): FlowchartNodeDto {
    for (const el of elements) {
      if (el.id === id) {
        return el;
      } else if (el.children) {
        const child = this.getFlowchartElement(el.children, id);
        if (child) {
          return child;
        }
      }
    }

    return null;
  }

  validateConnection(
    cellViewS: joint.dia.CellView,
    magnetS: SVGElement,
    cellViewT: joint.dia.CellView,
    magnetT: SVGElement,
    end: joint.dia.LinkEnd,
    linkView: joint.dia.LinkView,
    graph: joint.dia.Graph,
  ): boolean {
    if (LinkValidator.doElementsHaveLinkInBetween(graph, cellViewS, cellViewT.model.attributes.id)) {
      return false;
    }

    if (!LinkValidator.isTargetMagnetDefined(magnetT)) {
      return false;
    }

    if (!LinkValidator.doesLinkConnectToAnotherElement(cellViewS, cellViewT)) {
      return false;
    }

    if (!LinkValidator.doesLinkConnectToElement(cellViewS, cellViewT)) {
      return false;
    }

    if (!LinkValidator.allowConnectionOnElementBody(this.flowchartElementsSubjectStore, cellViewS, cellViewT, magnetS, magnetT)) {
      return false;
    }

    return LinkValidator.doesLinkConnectInsideElementWithSubflowElement(cellViewS, cellViewT);
  }

  protected handleComments(comments: AnchoredFlowchartComment[], selectedCommentId: string) {
    const allCells = this.graph?.getCells() || [];
    const allLinks = this.graph?.getLinks() || [];

    // Remove highlights from everything first
    allCells.forEach((cell) => this.removeHighlightFromElement(cell));
    allLinks.forEach((link) => this.removeHighlightFromElement(link));

    // Pair cells with all their comments
    const cellsWithComments = allCells
      .map((cell) => {
        const cellComments = comments.filter((comment) => comment.anchor.elementId === (cell.attr('body/unique-id') ?? cell.id));
        return cellComments.length ? { cell, comments: cellComments } : null;
      })
      .filter(Boolean) as { cell: any; comments: AnchoredFlowchartComment[] }[];

    // Pair links with all their comments
    const linksWithComments = allLinks
      .map((link) => {
        const linkComments = comments.filter((comment) => comment.anchor.elementId === link.id);
        return linkComments.length ? { link, comments: linkComments } : null;
      })
      .filter(Boolean) as { link: any; comments: AnchoredFlowchartComment[] }[];

    // Highlight cells
    cellsWithComments.forEach(({ cell, comments }) => {
      this.addHighlightsToElement(comments, cell, selectedCommentId);
    });

    // Highlight links
    linksWithComments.forEach(({ link, comments }) => {
      this.addHighlightsToElement(comments, link, selectedCommentId);
    });

    this.refreshCommentBadges();
  }

  private commentsPrefix = `tt-comments-flowchart-element`;

  private refreshCommentBadges(): void {
    if (!this.graph || !this.paperSource?.paper) return;

    const badges = this.graph.getCells().flatMap((cell) => {
      const elementId = cell.isLink() ? cell.id : (cell.attr('body/unique-id') ?? cell.id);
      const comments = this.comments().filter((comment) => comment.anchor.elementId === elementId);

      return comments.length
        ? [{ cell, count: comments.length, selected: comments.some((comment) => comment.id === this.selectedCommentId()) }]
        : [];
    });

    renderCommentBadges(this.paperSource.paper, badges);
  }

  private removeHighlightFromElement(element: joint.dia.Cell) {
    const classPath = element.attributes.type === 'standard.Link' ? 'attrs/line/class' : 'attrs/body/class';
    const currentClass = element.prop(classPath) || '';

    const cleanedClass = currentClass
      .split(' ')
      .filter(
        (cls) =>
          !cls.startsWith(this.commentsPrefix) &&
          !cls.startsWith('tt-comments-comment-') &&
          cls !== 'tt-comment-element--selected' &&
          cls !== 'tt-personal-note',
      )
      .join(' ')
      .trim();

    element.prop(classPath, cleanedClass);
  }

  private addHighlightToElement(comment: AnchoredFlowchartComment, element: joint.dia.Cell | joint.dia.Link, selected = false) {
    const classPath = element.attributes.type === 'standard.Link' ? 'attrs/line/class' : 'attrs/body/class';
    const currentClass = element.prop(classPath) || '';

    const commentedElementClass = `${this.commentsPrefix}-${comment.id}`;

    const classes = new Set(currentClass.split(' ').filter(Boolean));
    classes.add(commentedElementClass);
    classes.add(`tt-comments-comment-${comment.id}`);
    if (comment.type === CommentType.PersonalNote) {
      classes.add('tt-personal-note');
    }

    if (selected) {
      classes.add(`${this.commentsPrefix}--selected`);
    }

    if (element.attributes.type === 'standard.Link') {
      classes.add(`${this.commentsPrefix}-link`);
    }
    element.prop(classPath, Array.from(classes).join(' '));

    this.paperSource.paper.updateViews();
  }

  private addHighlightsToElement(
    comments: AnchoredFlowchartComment[],
    element: joint.dia.Cell | joint.dia.Link,
    selectedCommentId: string,
  ) {
    comments.forEach((comment) => this.addHighlightToElement(comment, element, comment.id === selectedCommentId));
  }

  public prepareViewportUpdate(position: { x: number; y: number }): void {
    this.viewportAnchor = this.paper.localToClientPoint(position.x, position.y);
  }

  public finalizeViewportUpdate(position: { x: number; y: number }): void {
    if (!this.viewportAnchor) return;

    const clientPoint = this.paper.localToClientPoint(position.x, position.y);
    const dx = clientPoint.x - this.viewportAnchor.x;
    const dy = clientPoint.y - this.viewportAnchor.y;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      this.scroller.el.scrollLeft += dx;
      this.scroller.el.scrollTop += dy;
    }
    this.viewportAnchor = null;
  }

  public unsubscribe() {
    this.flowchartEventsService.removeEventHandlers();
    this.dtoSubscriber && this.dtoSubscriber.unsubscribe();
    this.storeSubscriber && this.storeSubscriber.unsubscribe();
  }
}
